<template>
  <div v-rect-loading="loading" class="job-posting-channel-editor-page">
    <div class="job-posting-channel-editor-header">
      <div class="job-posting-channel-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="content">
      <WrappedFrom
        ref="form"
        class="form-editor"
        label-width="220px"
        :model="jobPostingChannelAccountData"
        :formLines="formLines"
        @save="handleSave"
      >
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { getI18nPropertyValue, selectLabelI18n } from '@/i18n'
import { CHANNEL_ID, DEFAULT_CHANNEL_ID } from '@/constant/job'
import { mapActions, mapGetters } from 'vuex'
import { passwordEncrypt } from '@/utils/rsa'

export default {
  components: {
    WrappedFrom
  },
  props: {

  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.jobPostingChannelAccount',
      jobPostingChannelAccountData: {
        job51MemberName: null,
        channel: null,
        username: null,
        password: null,
        isPublic: true,
        authorizedUser: null
      },
      channelList: [],
      userList: []
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    currentAccount () {
      return this.getCurrentAccount
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}.title.${this.isEditMode ? 'jobPostingChannelAccountEdit' : 'jobPostingChannelAccountAdd'}`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    formLines () {
      const vm = this
      const requiredMessage = this.$t('common.message.requiredFormField')
      return {
        channel: {
          elType: 'el-select',
          option: {
            data: this.channelList
          },
          label: this.$t(`${this.i18nPrefix}.label.channelName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.channelName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        job51MemberName: {
          get hidden () {
            return vm.jobPostingChannelAccountData.channel !== CHANNEL_ID.JOB51
          },
          props: {
            maxlength: 64
          },
          label: this.$t(`${this.i18nPrefix}.label.job51MemberName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.job51MemberName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        username: {
          label: this.$t(`${this.i18nPrefix}.label.username`),
          props: {
            maxlength: 64
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.username`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        password: {
          label: this.$t(`${this.i18nPrefix}.label.password`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.password`),
          props: {
            showPassword: true,
            maxlength: 64
          },
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        isPublic: {
          elType: 'el-radio-group',
          option: {
            data: [
              {
                label: this.$t(`${this.i18nPrefix}.label.authorizeAll`),
                value: true
              },
              {
                label: this.$t(`${this.i18nPrefix}.label.customAuthorization`),
                value: false
              }
            ]
          },
          label: this.$t(`${this.i18nPrefix}.label.authorizedUser`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.authorizedUser`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        authorizedUser: {
          get hidden () {
            return vm.jobPostingChannelAccountData.isPublic
          },
          elType: 'el-select',
          props: {
            filterable: true,
            multiple: true,
            clearable: true
          },
          option: {
            data: this.userList,
            labelKey: 'email_address',
            valueKey: 'id'
          },
          class: 'authorized-user',
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.authorizedUser`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        }
      }
    },
    postingChannelId () {
      return this.$route.params.id
    },
    hasCompanyApprovalPermission () {
      return this.hasAnyPermission('PERM_COMPANY_APPROVAL')
    }
  },
  created () {
    this.initData()
    this.loadCompanyJobPostChannelList()
  },
  mounted () {

  },
  methods: {
    ...mapActions('jobPostingChannelAccount', ['getJobPostingChannelAccount', 'createJobPostingChannelAccount', 'updateJobPostingChannelAccount']),
    ...mapActions('userManagement', ['getUserList']),
    ...mapActions('company', ['getCompanyJobPostingChannelList']),
    async initData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadJobPostingChannelAccount(),
          this.loadHRAccount()
        ])
      } finally {
        this.loading = false
      }
    },
    async loadJobPostingChannelAccount () {
      if (!this.isEditMode) {
        return
      }
      const resp = await this.getJobPostingChannelAccount(this.postingChannelId)
      if (resp) {
        this.jobPostingChannelAccountData = {
          channel: resp.channel_id,
          job51MemberName: resp.job_51_member_name,
          username: resp.username,
          password: resp.password,
          isPublic: resp.is_public,
          authorizedUser: _.map(resp.bind_accounts, 'id')
        }
      }
    },
    async loadCompanyJobPostChannelList () {
      const resp = await this.getCompanyJobPostingChannelList({ company_id: this.currentAccount.company_id })
      if (resp) {
        const vm = this
        this.channelList = _.chain(resp.post_channels)
          .filter(channel => channel.id !== DEFAULT_CHANNEL_ID && channel.manual_job_posting_can_use)
          .map(channel => {
            return {
              get label () {
                if (channel.id === CHANNEL_ID.BOSS) {
                  return vm.$t(`${vm.i18nPrefix}.label.boss`)
                } else if (channel.id === CHANNEL_ID.LIE_TOU) {
                  return vm.$t(`${vm.i18nPrefix}.label.lietou`)
                }
                return getI18nPropertyValue(channel, 'cn_name', 'name')
              },
              value: channel.id
            }
          })
          .value()
      }
    },
    async loadHRAccount () {
      const params = {
        status: 1
      }
      if (this.hasCompanyApprovalPermission) {
        params.type = 'premium_accounts'
      }
      const resp = await this.getUserList(params)
      if (resp) {
        this.userList = resp.account_list
      }
    },
    async handleSave () {
      let result = false
      result = await this.$refs.form.submit()
      if (!result) {
        return
      }
      const data = {
        channel_id: this.jobPostingChannelAccountData.channel,
        job_51_member_name: this.jobPostingChannelAccountData.job51MemberName,
        username: this.jobPostingChannelAccountData.username,
        password: passwordEncrypt(this.jobPostingChannelAccountData.password),
        is_public: this.jobPostingChannelAccountData.isPublic,
        bind_hr_account_ids: this.jobPostingChannelAccountData.isPublic ? [] : this.jobPostingChannelAccountData.authorizedUser
      }
      let params = {}
      let createOrUpdateMethod = null
      if (this.isEditMode) {
        params = {
          id: this.postingChannelId,
          data
        }
        createOrUpdateMethod = this.updateJobPostingChannelAccount
      } else {
        params = data
        createOrUpdateMethod = this.createJobPostingChannelAccount
      }
      this.loading = true
      try {
        const resp = await createOrUpdateMethod(params)
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
          this.$router.push({
            path: '/v-admin/job-posting-channel-account/management'
          })
        }
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      this.$router.push({
        path: '/v-admin/job-posting-channel-account/management'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.job-posting-channel-editor-page {
  .job-posting-channel-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .job-posting-channel-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .content {
    width: 606px;
    margin: 30px auto 0;

    .form-editor {
      &.form-container.asterisk-after {
        ::v-deep {
          .authorized-user {
            .el-form-item__label {
              &::after {
                visibility: hidden;
              }
            }
          }
        }
      }
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
